import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/Seo";
import MoonMark from "@/components/MoonMark";
import { Reveal } from "@/components/Reveal";
import { Divider, Glyph, TattooBanner } from "@/components/Ornaments";
import { useT, Rich } from "@/components/contentContext";

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICES = [
  {
    name: "Tabletop RPG Background Music",
    from: 900,
    desc: "Looping dungeon ambience, tavern warmth and battle stingers composed for your table — sessions that score themselves.",
  },
  {
    name: "Original Multimedia Soundtrack",
    from: 2500,
    desc: "A bespoke score for your film, game or streamed campaign — themes that breathe with the edit and bloom where they must.",
  },
  {
    name: "Game Design Document Review",
    from: 450,
    desc: "A composer's and director's eye over your GDD — pacing, tone, and the seams players will feel.",
  },
  {
    name: "Game Design Document Writing",
    from: 1200,
    desc: "Systems, narrative and audio direction written into clean, buildable documents.",
  },
  {
    name: "Creative Direction",
    from: 3000,
    desc: "Vision to ship — brand voice, sound and art aligned under one dedicated gaze.",
  },
];

const TIMELINES = [
  { label: "Tight", desc: "Under 14 days", note: "For sessions that need music before the next full moon." },
  { label: "Standard", desc: "About 30 days", note: "The sweet spot — room for revision rounds and polish." },
  { label: "Flexible", desc: "60+ days", note: "Long arcs get the deepest development, at the calmest pace." },
];

const PROJECT_TYPES = [
  "Curse of Strahd / Ravenloft custom track",
  "TTRPG session ambience",
  "Boss battle / combat theme",
  "Campaign suite (multi-track)",
  "Indie game soundtrack",
  "Something else",
];
const DEADLINES = ["Flexible · 60+ days", "Standard · ~30 days", "Tight · under 14 days", "Not sure yet"];
const BUDGETS = ["Under $250", "$250 – $900", "$900 – $2,500", "$2,500+", "Let's discuss"];

const STRAHD_VIDEO = { id: "a2r2_KWFgFM", title: "Delighted to Enchant You — Curse of Strahd Unofficial Ambience" };

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const inputCls = "input-night";

const InquiryForm = () => {
  const t = useT();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    project_type: PROJECT_TYPES.includes(params.get("type") || "") ? params.get("type") : "",
    mood_style: params.get("mood") || "",
    length: "",
    deadline: "",
    budget: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.project_type) {
      setError("Name, email and project type are needed to open the conversation.");
      return;
    }
    setBusy(true);
    try {
      const message = [
        `— Commission inquiry —`,
        `Project type: ${form.project_type}`,
        form.mood_style && `Mood / style: ${form.mood_style}`,
        form.length && `Length: ${form.length}`,
        form.deadline && `Deadline: ${form.deadline}`,
        form.budget && `Budget: ${form.budget}`,
        form.notes && `Notes: ${form.notes}`,
      ]
        .filter(Boolean)
        .join("\n");
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message,
          quote: { type: "commission-inquiry", ...form },
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Sending failed");
      setSent(true);
    } catch (err) {
      setError(err.message || "Sending failed.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="border border-gold/40 bg-[#0d0d12] p-10 text-center" data-testid="commission-form-success">
        <MoonMark className="mx-auto h-10 w-10 text-gold" />
        <h3 className="mt-6 font-display text-xl tracking-[0.15em] text-silver">{t("commission.success.heading")}</h3>
        <Rich
          as="p"
          text={t("commission.success.body")}
          className="mx-auto mt-4 max-w-md text-sm font-light leading-relaxed text-slate-400"
        />
        <Link to="/portfolio" className="btn-ghost-night mt-8" data-testid="success-cta-portfolio">
          Hear the Work While You Wait
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-line bg-surface p-6 md:p-10" data-testid="commission-form">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Your name *</label>
          <input className={inputCls} value={form.name} onChange={set("name")} data-testid="commission-form-name" placeholder="Eridor of Barovia" />
        </div>
        <div>
          <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Email *</label>
          <input className={inputCls} type="email" value={form.email} onChange={set("email")} data-testid="commission-form-email" placeholder="you@example-table.com" />
        </div>
        <div>
          <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Project type *</label>
          <select className={inputCls} value={form.project_type} onChange={set("project_type")} data-testid="commission-form-project-type">
            <option value="">Choose the shape of the work…</option>
            {PROJECT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Desired mood &amp; style</label>
          <input className={inputCls} value={form.mood_style} onChange={set("mood_style")} data-testid="commission-form-mood-style" placeholder="Warm gothic, moonlit strings, hopeful horns, organ & cello…" />
        </div>
        <div>
          <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Expected length</label>
          <input className={inputCls} value={form.length} onChange={set("length")} data-testid="commission-form-length" placeholder="e.g. 3-minute seamless loop, 10-min suite…" />
        </div>
        <div>
          <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Deadline</label>
          <select className={inputCls} value={form.deadline} onChange={set("deadline")} data-testid="commission-form-deadline">
            <option value="">When does the night need it?</option>
            {DEADLINES.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Budget</label>
          <select className={inputCls} value={form.budget} onChange={set("budget")} data-testid="commission-form-budget">
            <option value="">A comfortable range…</option>
            {BUDGETS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-2.5 block font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Anything else</label>
          <input className={inputCls} value={form.notes} onChange={set("notes")} data-testid="commission-form-notes" placeholder="Party size, key scenes, references…" />
        </div>
      </div>
      {error && (
        <p className="mt-5 text-xs text-red-500" data-testid="commission-form-error">
          {error}
        </p>
      )}
      <button className="btn-night mt-8 w-full md:w-auto" type="submit" disabled={busy} data-testid="commission-form-submit">
        {busy ? "Sending the raven…" : "Send the Inquiry"}
      </button>
      <p className="mt-4 text-[10px] leading-relaxed text-slate-600">
        No account needed. This opens a conversation — you will receive a personal reply and a firm quote before any money moves.
      </p>
    </form>
  );
};

const StrahdVideo = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-10">
      <button
        className="group relative block w-full cursor-pointer overflow-hidden border border-line"
        onClick={() => setOpen(true)}
        data-testid="strahd-video-open"
        aria-label="Play the Curse of Strahd ambience sample"
      >
        <img
          src={`https://i.ytimg.com/vi/${STRAHD_VIDEO.id}/hqdefault.jpg`}
          alt="Curse of Strahd ambience sample — Delighted to Enchant You"
          data-testid="strahd-video-thumb"
          className="aspect-video w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          style={{ filter: "brightness(0.85)" }}
        />
        <span className="absolute inset-0 flex items-center justify-center bg-ink/30 transition-colors duration-500 group-hover:bg-ink/10" aria-hidden="true">
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/70 bg-ink/80 text-gold backdrop-blur">
            <svg viewBox="0 0 24 24" className="ml-1 h-6 w-6 fill-current" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
          </span>
        </span>
      </button>
      <p className="mt-3 font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">
        {STRAHD_VIDEO.title} — the piece behind the channel
      </p>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/92 p-4 backdrop-blur-xl" onClick={() => setOpen(false)} data-testid="strahd-video-lightbox">
          <div className="relative w-full max-w-4xl border border-gold/30 bg-surface p-3 md:p-4" onClick={(e) => e.stopPropagation()}>
            <button className="absolute -top-12 right-0 cursor-pointer text-slate-400 transition-colors hover:text-silver" onClick={() => setOpen(false)} aria-label="Close" data-testid="strahd-video-close">
              <svg viewBox="0 0 24 24" className="h-6 w-6" stroke="currentColor" fill="none" strokeWidth="2" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
            <div className="aspect-video w-full bg-black">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${STRAHD_VIDEO.id}?autoplay=1&rel=0`}
                title={STRAHD_VIDEO.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Commission = () => {
  const t = useT();
  const [tracks, setTracks] = useState([]);
  const [testimonials, setTestimonials] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/tracks`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((d) => setTracks((d || []).slice(0, 3)))
      .catch(() => setTracks([]));
    fetch(`${API}/api/testimonials`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((d) => setTestimonials(d || []))
      .catch(() => setTestimonials([]));
  }, []);

  return (
    <main>
      <Seo
        title="Commission Custom Music — Custom D&D Music & TTRPG Ambience"
        description="Commission custom D&D music: dungeon ambience, boss battle themes and Curse of Strahd suites composed to invoke the emotions your story desires. Pricing from $450, personal reply to every inquiry."
      />

      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden pt-40 pb-24 md:pt-52 md:pb-32" data-testid="commission-hero">
        <div className="starfield absolute inset-0" aria-hidden="true" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow flex items-center gap-3">
              <Glyph className="h-3 w-3 text-gold" />
              Commission Bespoke Music
            </p>
            <h1 className="mt-8 max-w-3xl font-display text-4xl font-bold leading-[1.02] text-silver sm:text-5xl lg:text-6xl">
              {t("commission.hero.heading")}
            </h1>
            <Rich
              as="p"
              text={t("commission.hero.body")}
              className="mt-8 max-w-2xl text-base font-light leading-relaxed text-slate-400 md:text-lg"
            />
            <div className="mt-12 flex flex-wrap items-center gap-5">
              <a href="#commission-form" className="btn-night" data-testid="hero-cta-inquiry">
                Start the Inquiry
              </a>
              <Link to="/portfolio" className="btn-ghost-night" data-testid="hero-cta-gallery">
                Hear the Work First
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- WHAT / WHO / WHY ---------- */}
      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-32 lg:px-10" data-testid="commission-why">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {[
            { title: t("commission.why.card1_title"), body: t("commission.why.card1_body") },
            { title: t("commission.why.card2_title"), body: t("commission.why.card2_body") },
            { title: t("commission.why.card3_title"), body: t("commission.why.card3_body") },
          ].map((c, i) => (
            <Reveal key={c.title + i} delay={0.1 * i}>
              <div className="h-full border border-line bg-surface p-8 transition-colors duration-500 hover:border-gold/40">
                <MoonMark className="h-7 w-7 text-gold/80" />
                <h2 className="mt-6 font-display text-xl tracking-[0.1em] text-silver">{c.title}</h2>
                <Rich as="p" text={c.body} className="mt-4 text-sm font-light leading-relaxed text-slate-400" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <TattooBanner className="my-4" />

      {/* ---------- CURSE OF STRAHD ---------- */}
      <section className="border-y border-line bg-[#0b0709] py-24 md:py-32" data-testid="strahd-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <p className="eyebrow flex items-center gap-3">
              <Glyph className="h-3 w-3 text-red-500" />
              For the Masters of Ravenloft
            </p>
            <h2 className="mt-6 font-display text-3xl leading-snug text-silver md:text-4xl">
              {t("commission.strahd.heading")}
            </h2>
            <Rich
              as="p"
              text={t("commission.strahd.body")}
              className="mt-6 max-w-xl text-base font-light leading-relaxed text-slate-400"
            />
            <ul className="mt-8 space-y-3">
              {t("commission.strahd.bullets")
                .split("\n")
                .filter(Boolean)
                .map((h) => (
                  <li key={h} className="flex items-start gap-3 text-sm font-light text-slate-300">
                    <Glyph className="mt-1 h-3 w-3 shrink-0 text-gold" />
                    {h}
                  </li>
                ))}
            </ul>
            <a href="#commission-form" className="btn-night mt-10" data-testid="strahd-commission-cta">
              Commission a Strahd Track
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <StrahdVideo />
          </Reveal>
        </div>
      </section>

      {/* ---------- SERVICES & PRICING RANGES ---------- */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10" data-testid="commission-pricing">
        <Reveal>
          <p className="eyebrow">Services &amp; Pricing</p>
          <h2 className="mt-6 max-w-2xl font-display text-3xl leading-snug text-silver md:text-5xl">
            {t("commission.pricing.heading")}
          </h2>
          <Rich
            as="p"
            text={t("commission.pricing.body")}
            className="mt-6 max-w-2xl text-base font-light leading-relaxed text-slate-400"
          />
          <Link
            to="/services"
            className="mt-4 inline-block font-jmono text-[10px] uppercase tracking-[0.3em] text-gold underline-offset-4 hover:underline"
            data-testid="pricing-calc-link"
          >
            Open the quote calculator →
          </Link>
        </Reveal>
        <div className="mt-14 space-y-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.name} delay={0.05 * i}>
              <div className="flex flex-wrap items-center justify-between gap-4 border border-line bg-surface px-6 py-6 transition-colors duration-500 hover:border-gold/40 md:px-8">
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-base tracking-[0.08em] text-silver md:text-lg">{s.name}</h3>
                  <p className="mt-2 max-w-xl text-sm font-light leading-relaxed text-slate-500">{s.desc}</p>
                </div>
                <p className="font-jmono text-sm tracking-[0.15em] text-gold" data-testid={`pricing-from-${i}`}>
                  from {fmt(s.from)}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {TIMELINES.map((tl) => (
              <div key={tl.label} className="border border-line bg-[#0d0d12] p-6">
                <p className="font-jmono text-[10px] uppercase tracking-[0.3em] text-gold">{tl.label} · {tl.desc}</p>
                <p className="mt-3 text-sm font-light leading-relaxed text-slate-400">{tl.note}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ---------- PAST WORK ---------- */}
      {tracks.length > 0 && (
        <section className="border-y border-line bg-[#090910] py-24 md:py-32" data-testid="commission-past-work">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <Reveal>
              <p className="eyebrow">Recent Work</p>
              <h2 className="mt-6 max-w-2xl font-display text-3xl leading-snug text-silver md:text-4xl">
                {t("commission.pastwork.heading")}
              </h2>
              <Rich
                as="p"
                text={t("commission.pastwork.body")}
                className="mt-6 max-w-2xl text-base font-light leading-relaxed text-slate-400"
              />
            </Reveal>
            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {tracks.map((trk, i) => (
                <Reveal key={trk.id} delay={0.08 * i}>
                  <div className="h-full border border-line bg-surface p-6" data-testid={`past-work-${i}`}>
                    <p className="font-jmono text-[9px] uppercase tracking-[0.3em] text-gold/70">{trk.title ? "Track" : ""}</p>
                    <h3 className="mt-3 font-display text-lg leading-snug text-silver">{trk.title}</h3>
                    {trk.description && (
                      <p className="mt-3 text-sm font-light italic leading-relaxed text-slate-500">{t.description}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <div className="mt-12 text-center">
                <Link to="/portfolio" className="btn-ghost-night" data-testid="past-work-cta-gallery">
                  Walk the Full Gallery
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ---------- TESTIMONIALS ---------- */}
      {testimonials && testimonials.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10" data-testid="commission-testimonials">
          <Reveal>
            <p className="eyebrow">From the Table</p>
            <h2 className="mt-6 max-w-2xl font-display text-3xl leading-snug text-silver md:text-4xl">
              {t("commission.testimonials.heading")}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={0.08 * (i % 2)}>
                <figure className="h-full border-l-2 border-gold/50 bg-surface p-8" data-testid={`testimonial-${i}`}>
                  <blockquote className="font-cormorant text-xl italic leading-relaxed text-slate-300">
                    "{t.quote}"
                  </blockquote>
                  <figcaption className="mt-6 font-jmono text-[10px] uppercase tracking-[0.3em] text-gold">
                    {t.author}
                    {t.source && <span className="ml-2 text-slate-600">· {t.source}</span>}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <Divider className="mx-auto max-w-md" />

      {/* ---------- SUPPORT THE NIGHT ---------- */}
      <section className="mx-auto max-w-4xl px-6 pt-24 lg:px-10" data-testid="commission-support">
        <Reveal>
          <div className="flex flex-col items-center gap-6 border border-gold/25 bg-[#0d0d12] px-8 py-12 text-center">
            <MoonMark className="h-8 w-8 text-gold" />
            <div>
              <h2 className="font-display text-2xl text-silver">{t("commission.support.heading")}</h2>
              <Rich
                as="p"
                text={t("commission.support.body")}
                className="mx-auto mt-3 max-w-lg text-sm font-light leading-relaxed text-slate-400"
              />
            </div>
            <button
              className="btn-night"
              onClick={() => window.dispatchEvent(new Event("nox-open-donate"))}
              data-testid="commission-support-btn"
            >
              Leave an Offering
            </button>
          </div>
        </Reveal>
      </section>

      {/* ---------- INQUIRY FORM ---------- */}
      <section className="mx-auto max-w-4xl px-6 py-24 md:py-32 lg:px-10" data-testid="commission-inquiry">
        <Reveal>
          <p className="eyebrow text-center">Begin the Commission</p>
          <h2 className="mt-6 text-center font-display text-3xl leading-snug text-silver md:text-4xl">
            {t("commission.inquiry.heading")}
          </h2>
          <Rich
            as="p"
            text={t("commission.inquiry.body")}
            className="mx-auto mt-6 max-w-xl text-center text-base font-light leading-relaxed text-slate-400"
          />
        </Reveal>
        <div className="mt-14">
          <InquiryForm />
        </div>
      </section>
    </main>
  );
};

export default Commission;
